import { Injectable } from '@angular/core';
import { HttpService } from '../app/utility/http-service';

export interface DataSetDto {
  id: string;
  name: string;
}

export interface PartnerUserDto {
  id: string;
  name: string;
  email: string;
}

export interface ClientOptionDto {
  id: string;
  displayName: string;
}

export interface CreatePartnerRequest {
  entityCode: string;
  entityName: string;
  refClientId?: string | null;
}

export interface PartnerDto {
  id: string;
  code: string;
  name: string;
  refClientId?: string;
  refClientName?: string;
}

export interface RecordAccessNodesResult {
  hierarchies: AccessNodeDto[];
  attachments: AccessNodeDto[];
}
export interface PartnersResult {
  partners: PartnerDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface PartnerAttachmentDto {
  recordId: string;
  attachmentId: string;
  fileName: string;
  hierarchyId?: string;
}

export interface AttachmentFileDto {
  id: string;
  fileName: string;
  description?: string;
  effectiveDate?: string;
  fileSizeBytes?: number;
  hierarchyId?: string;
}

export interface AttachmentFolderDto {
  id: string;
  folderName: string;
  children: AttachmentFolderDto[];
  attachments: AttachmentFileDto[];
}
export interface AttachmentAccessDto {
  id: string;
  allowDownload: boolean;
  allowUpload: boolean;
  allowUpdate: boolean;
  negate: boolean;
  recordId: string;
}

export interface HierarchyAccessDto {
  id: string;
  negate: boolean;
  recordId: string;
}

export interface PartnerAccessMatrixVm {
  partnerId: string;
  recordIds: string[];
  attachments: AttachmentAccessDto[];
  hierarchies: HierarchyAccessDto[];
}

export interface PolicyRecordDto {
  id: string;
  fields: Record<string, any>;
}

export interface PartnerRecordSummaryDto {
  id: string;
  displayName: string | null;
}

export interface PolicyRecordsResult {
  fieldNames: string[];
  records: PolicyRecordDto[];

  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface SavePartnerAccessRequest {
  partnerIds: string[];
  recordIds: string[];
  dataPolicyId: string;
}

export interface AccessNodeDto {
  id: string;
  negate: boolean;
  type: string;
}

export interface RevokePartnerAssignmentsRequest {
  partnerId: string;
  dataPolicyId: string;

  recordIds?: string[];
  attachmentIds?: string[];
  hierarchyIds?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PartnerAccessService {
  private readonly basePath = 'PartnerAccess';

  constructor(private http: HttpService) { }

  getAllPartners(
    clientId: string,
    onSuccess: (res: PartnerDto[]) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetAllPartners?clientId=${encodeURIComponent(clientId)}`;
    this.http.get<PartnerDto[]>(uri, onSuccess, true);
  }


  getDataSets(
    clientId: string,
    onSuccess: (res: DataSetDto[]) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetDataSets?clientId=${encodeURIComponent(clientId)}`;
    this.http.get<DataSetDto[]>(uri, onSuccess, true);
  }

  getDataPolicyId(
    clientId: string,
    onSuccess: (id: string) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetDataPolicyId?clientId=${clientId}`;
    this.http.get<string>(uri, onSuccess, true);
  }

  getPolicyRecords(
    clientId: string,
    dataSetId: string | null | undefined,
    onSuccess: (res: PolicyRecordsResult) => void,
    onError?: () => void,
    page = 1,
    pageSize = 25,
    search?: string
  ): void {
    let uri = `${this.basePath}/GetPolicyRecords?clientId=${encodeURIComponent(clientId)}`;

    if (dataSetId) {
      uri += `&dataSetId=${encodeURIComponent(dataSetId)}`;
    }

    uri += `&page=${page}&pageSize=${pageSize}`;

    if (search) uri += `&search=${encodeURIComponent(search)}`;

    this.http.get<PolicyRecordsResult>(uri, onSuccess, true);
  }

  getPartnerRecordAccess(
    clientId: string,
    partnerId: string,
    onSuccess: (recordIds: string[]) => void,
    onError?: () => void
  ): void {
    const uri =
      `${this.basePath}/GetPartnerRecordAccess` +
      `?clientId=${encodeURIComponent(clientId)}` +
      `&partnerId=${encodeURIComponent(partnerId)}`;

    this.http.get<string[]>(uri, onSuccess, true);
  }

  getAccessMatrix(
    clientId: string,
    dataPolicyId: string,
    partnerId: string,
    onSuccess: (res: PartnerAccessMatrixVm) => void,
    onError?: () => void
  ): void {
    const uri =
      `${this.basePath}/GetAccessMatrix` +
      `?clientId=${encodeURIComponent(clientId)}` +
      `&dataPolicyId=${encodeURIComponent(dataPolicyId)}` +
      `&partnerId=${encodeURIComponent(partnerId)}`;

    this.http.get<PartnerAccessMatrixVm>(uri, onSuccess, true);
  }

  revokeAccess(
    clientId: string,
    payload: RevokePartnerAssignmentsRequest,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/Revoke?clientId=${encodeURIComponent(clientId)}`;
    this.http.postReturn(uri, onSuccess, undefined, payload, onError);
  }

  saveHierarchyAccess(
    partnerId: string,
    recordId: string,
    nodes: AccessNodeDto[],
    onSuccess: () => void,
    onError?: () => void
  ): void {

    const uri =
      `${this.basePath}/SaveHierarchyAccess` +
      `?partnerId=${encodeURIComponent(partnerId)}` +
      `&recordId=${encodeURIComponent(recordId)}`;

    this.http.postReturn(uri, onSuccess, undefined, nodes, onError);
  }

  saveAttachmentAccess(
    partnerId: string,
    recordId: string,
    nodes: AccessNodeDto[],
    onSuccess: () => void,
    onError?: () => void
  ): void {

    const uri =
      `${this.basePath}/SaveAttachmentAccess` +
      `?partnerId=${encodeURIComponent(partnerId)}` +
      `&recordId=${encodeURIComponent(recordId)}`;

    this.http.postReturn(uri, onSuccess, undefined, nodes, onError);
  }

  getHierarchyTree(
    clientId: string,
    recordId: string,
    onSuccess: (res: AttachmentFolderDto[]) => void,
    onError?: () => void,
    dataSetId?: string | null
  ): void {
    let uri =
      `api/attachments/hierarchy-tree` +
      `?clientId=${encodeURIComponent(clientId)}` +
      `&recordId=${encodeURIComponent(recordId)}`;

    if (dataSetId) uri += `&dataSetId=${encodeURIComponent(dataSetId)}`;

    this.http.get<AttachmentFolderDto[]>(uri, onSuccess, true);
  }

  getRecordAttachments(
    clientId: string,
    recordId: string,
    onSuccess: (res: AttachmentFileDto[]) => void,
    onError?: () => void,
    dataSetId?: string | null
  ): void {
    const parts = [
      `clientId=${encodeURIComponent(clientId)}`,
      `recordId=${encodeURIComponent(recordId)}`
    ];
    if (dataSetId?.trim()) parts.push(`dataSetId=${encodeURIComponent(dataSetId)}`);
    this.http.get<AttachmentFileDto[]>(`api/attachments/with-hierarchy?${parts.join('&')}`, onSuccess, true);
  }

  savePartnerRecordAccess(
    clientId: string,
    payload: SavePartnerAccessRequest,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri =
      `${this.basePath}/SavePartnerRecordAccess` +
      `?clientId=${encodeURIComponent(clientId)}`;

    this.http.postReturn(uri, onSuccess, undefined, payload, onError);
  }

  createPartner(
    clientId: string,
    request: CreatePartnerRequest,
    onSuccess: (res: { id: string }) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/CreatePartner?clientId=${encodeURIComponent(clientId)}`;
    this.http.postReturn(uri, onSuccess, undefined, request, onError);
  }

  deletePartner(
    partnerId: string,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/DeletePartner?partnerId=${encodeURIComponent(partnerId)}`;
    this.http.deleteReturn(uri, onSuccess, onError);
  }

  getPartnerUsers(
    partnerId: string,
    onSuccess: (res: PartnerUserDto[]) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetPartnerUsers?partnerId=${encodeURIComponent(partnerId)}`;
    this.http.get<PartnerUserDto[]>(uri, onSuccess, true);
  }

  getUsersForClient(
    clientId: string,
    onSuccess: (res: PartnerUserDto[]) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetUsersForClient?clientId=${encodeURIComponent(clientId)}`;
    this.http.get<PartnerUserDto[]>(uri, onSuccess, true);
  }

  getAssignableUsersForPartner(
    partnerId: string,
    clientId: string,
    onSuccess: (res: PartnerUserDto[]) => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/GetAssignableUsersForPartner?partnerId=${encodeURIComponent(partnerId)}&clientId=${encodeURIComponent(clientId)}`;
    this.http.get<PartnerUserDto[]>(uri, onSuccess, true);
  }

  addPartnerUser(
    partnerId: string,
    userId: string,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/AddPartnerUser?partnerId=${encodeURIComponent(partnerId)}&userId=${encodeURIComponent(userId)}`;
    this.http.postReturn(uri, onSuccess, undefined, {}, onError);
  }

  removePartnerUser(
    partnerId: string,
    userId: string,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/RemovePartnerUser?partnerId=${encodeURIComponent(partnerId)}&userId=${encodeURIComponent(userId)}`;
    this.http.deleteReturn(uri, onSuccess, onError);
  }

  getRecordsByIds(
    clientId: string,
    dataSetId: string | null | undefined,
    recordIds: string[],
    onSuccess: (res: PolicyRecordDto[]) => void,
    onError?: () => void
  ): void {
    let uri = `${this.basePath}/GetRecordsByIds?clientId=${encodeURIComponent(clientId)}`;
    if (dataSetId) uri += `&dataSetId=${encodeURIComponent(dataSetId)}`;
    this.http.postReturn(uri, onSuccess, undefined, recordIds, onError);
  }

  getAvailableClients(
    onSuccess: (res: ClientOptionDto[]) => void,
    onError?: () => void
  ): void {
    this.http.get<ClientOptionDto[]>('ClientSettings/GetPossibleClients', onSuccess, false);
  }

  assignClient(
    partnerId: string,
    refClientId: string | null,
    onSuccess: () => void,
    onError?: () => void
  ): void {
    const uri = `${this.basePath}/AssignClient?partnerId=${encodeURIComponent(partnerId)}`;
    this.http.post(uri, onSuccess, onError, { refClientId });
  }

  getAccessNodesByRecord(
    partnerId: string,
    recordId: string,
    onSuccess: (res: RecordAccessNodesResult) => void,
    onError?: () => void
  ): void {
    const uri =
      `${this.basePath}/GetAccessNodesByRecord` +
      `?partnerId=${encodeURIComponent(partnerId)}` +
      `&recordId=${encodeURIComponent(recordId)}`;

    this.http.get<RecordAccessNodesResult>(uri, onSuccess, true);
  }

}
